<template>
  <article class="stay-card">
    <div class="stay-photo">
      <img v-if="stayImage" :src="stayImage" :alt="hotel.name" />
    </div>
    <div class="stay-body">
      <h3 class="stay-name">{{ hotel.name }}</h3>
      <p v-if="hotel.address" class="stay-location">
        {{ hotel.address }}
      </p>
      <div v-if="promotions.length" class="stay-tags">
        <span v-for="promo in promotions" :key="promo.name" class="stay-tag">
          {{ promo.name }}
        </span>
      </div>
      <div class="stay-bottom">
        <div v-if="priceStatus === 'success'" class="stay-price">
          <p class="stay-price-label">
            {{ t("shell.worldmc.home.moreStaysFrom") }}
          </p>
          <p class="stay-price-line">
            <Price
              :currency="currency"
              :price="hotel.minBasePrice"
              :decimal="2"
              format="0,0"
              font-size="20px"
              color="#0a4486"
            />
            <span>
              {{ t("shell.worldmc.home.moreStaysPerNight") }}
            </span>
          </p>
        </div>
        <p v-else-if="priceStatus === 'soldOut'" class="stay-sold-out">
          {{ t("shared.hotel.hotelCard.soldOut") }}
        </p>
        <button type="button" class="stay-view" @click="emit('select', hotel)">
          {{ t("shell.worldmc.home.moreStaysViewRooms") }} →
        </button>
      </div>
    </div>
  </article>
</template>

<script setup>
import Price from "@ui/Price.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  /** booking/hotel/search 返回的酒店对象（含 minBasePrice 报价） */
  hotel: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(["select"]);

const { t } = useI18n();

const stayImage = computed(
  () => props.hotel?.videos?.[0]?.coverUrl || props.hotel?.images?.[0] || ""
);

const promotions = computed(() =>
  (props.hotel?.promotions || [])
    .filter(promo => promo?.name && promo.name.trim())
    .slice(0, 2)
);

const priceStatus = computed(() => {
  const price = props.hotel?.minBasePrice;
  if (typeof price === "undefined") return "loading";
  if (price === 0 || price === null) return "soldOut";
  return "success";
});

const currency = computed(
  () => props.hotel?.minBasePriceDetail?.approximate?.currency || ""
);

defineOptions({ name: "WorldmcStayCard" });
</script>

<style lang="scss" scoped>
.stay-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: 0 2px 10px rgba(13, 31, 43, 0.06);
}

.stay-photo {
  height: 222px;
  overflow: hidden;
  background: #d9d9d9;
}

.stay-photo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.stay-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 223px;
  padding: 24px;
}

.stay-name {
  margin: 0;
  color: #0d1f2b;
  font-family: "Cormorant-Bold", "Cormorant", serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.stay-location {
  margin: 10px 0 0;
  color: #0a4486;
  font-family: "lato", sans-serif;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stay-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.stay-tag {
  padding: 5px 10px;
  border: 1px solid #d6e0e5;
  color: #294757;
  font-family: "lato", sans-serif;
  font-size: 12px;
  line-height: 1;
}

.stay-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 24px;
}

.stay-price-label {
  margin: 0 0 2px;
  color: #576b78;
  font-family: "lato", sans-serif;
  font-size: 11px;
}

.stay-price-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
}

.stay-price-line span {
  color: #0a4486;
  font-family: "lato", sans-serif;
  font-size: 13px;
}

.stay-sold-out {
  margin: 0;
  color: #576b78;
  font-family: "lato", sans-serif;
  font-size: 13px;
}

.stay-view {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  color: #0a4486;
  background: none;
  cursor: pointer;
  font-family: "lato", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.stay-view:hover {
  color: #1876ae;
}
</style>
